/**
 * BookingDetailPanel — the side Sheet for a single booking: overview fields plus
 * the submit/confirm/cancel actions (non-optimistic; confirm/cancel are gated by
 * a confirmation Dialog inside BookingActions).
 */

import { useFormatters, useT } from '@hostesa/i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  StatusBadge,
} from '@hostesa/ui';
import { useHotelContext } from '../../lib/hotel-context';
import type { Booking } from './_deps';
import { asString } from './_deps';
import { BookingActions } from './booking-actions';
import { bookingStatusVariant } from './booking-status';
import { namespace } from './i18n';

interface FieldRowProps {
  label: string;
  value: string | undefined;
  fallback: string;
  testId: string;
}

function FieldRow({ label, value, fallback, testId }: FieldRowProps) {
  const present = value !== undefined && value.length > 0;
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5" data-testid={testId}>
      <dt className="text-sm text-[var(--color-stone-mid)]">{label}</dt>
      <dd
        className={
          present
            ? 'text-sm text-[var(--color-charcoal)] text-right'
            : 'text-sm italic text-[var(--color-warm-stone-deep)] text-right'
        }
      >
        {present ? value : fallback}
      </dd>
    </div>
  );
}

export interface BookingDetailPanelProps {
  booking: Booking | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BookingDetailPanel({ booking, open, onOpenChange }: BookingDetailPanelProps) {
  const { t } = useT(namespace);
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { formatDate } = useFormatters(timezone);

  if (!booking) return null;

  const checkIn = asString(booking.checkIn);
  const checkOut = asString(booking.checkOut);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="booking-detail-panel"
        className="w-full sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>{t('detail.title')}</SheetTitle>
          <div className="pt-1">
            <StatusBadge
              data-testid="booking-detail-status"
              status={bookingStatusVariant(booking.status)}
              label={t(`status.${booking.status}`)}
            />
          </div>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-6 pb-8">
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.overview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <FieldRow
                  testId="booking-field-check-in"
                  label={t('detail.checkIn')}
                  value={checkIn ? formatDate(checkIn) : undefined}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="booking-field-check-out"
                  label={t('detail.checkOut')}
                  value={checkOut ? formatDate(checkOut) : undefined}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="booking-field-room-type"
                  label={t('detail.roomType')}
                  value={asString(booking.roomType)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="booking-field-confirmation-mode"
                  label={t('confirmationMode.label')}
                  value={t(`confirmationMode.${booking.confirmationMode}`)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="booking-field-pms-id"
                  label={t('detail.externalPmsId')}
                  value={asString(booking.externalPmsId)}
                  fallback={t('detail.notProvided')}
                />
                <FieldRow
                  testId="booking-field-reservation"
                  label={t('detail.reservation')}
                  value={asString(booking.reservationRequestId)}
                  fallback={t('detail.notProvided')}
                />
              </dl>
            </CardContent>
          </Card>

          <Card data-testid="booking-actions-card">
            <CardHeader>
              <CardTitle>{t('actions.label')}</CardTitle>
            </CardHeader>
            <CardContent>
              <BookingActions tenantId={tenantId} hotelId={hotelId} booking={booking} />
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}
